import { Grid, Skeleton, TextField, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useNavigate } from "react-router-dom";
import { BankAccountProfileService } from "subModule/src/core/services";
import { useTranslation } from "react-i18next";
import DynamicSelect from "common/Select/Select";
import { useGetBankAccountsDropdownService } from "subModule/src/services/bankaccount/useBankAccount";
import MoveDownOutlinedIcon from "@mui/icons-material/MoveDownOutlined";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import SimpleModal from "common/Form/Form";
import {
  GetAccountTransferByIdResponse,
  GetBankAccountBalanceResponse,
} from "subModule/src/services/bankaccount/interface";
import { brandingInfo } from "features/server/brandingSlice";
import { useSelector } from "react-redux";
import { themes } from "theme/themes";
import { selectUserInfo } from "features/user/userSlice";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
interface FormData {
  [key: string]: any;
}
const InterAccountTransferCreate: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const loggedInUserCurrency = useSelector(selectUserInfo)?.business?.currency;
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = useForm<FormData>({ defaultValues: {} });
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [dropdownPage, setDropdownPage] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { data: bankAccountsData, refetch: bankAccountsRefetch } =
    useGetBankAccountsDropdownService({
      pageNumber: dropdownPage,
      size: 50,
      search: dropdownSearchTerm,
    });
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(true);
  const { breadcrumbItems } = useBusinessSetupEntityBreadcrumbs({
    moduleLabelKey: "interAccountTransfers",
    moduleHref: "/inter-account-transfers",
    isEdit: false,
  });
  useEffect(() => {
    bankAccountsRefetch();
  }, []);
  const accounts = (bankAccountsData?.pages[0]?.data?.content || []).map(
    (item: { id: number; accountTitle: string }) => ({
      value: item.id,
      label: item.accountTitle,
    }),
  );
  const destinationAccounts = accounts.filter(
    (option) => option.value !== watch("fromAccountId")?.value,
  );
  const getAccountBalanceById = (id: number) => {
    BankAccountProfileService.getAccountBalance(id)
      .then((response: GetBankAccountBalanceResponse) => {
        if (response.success) {
          setBalance(response?.data || 0);
        } else {
          enqueueSnackbar(<AlertMessages text={response?.message} />, {
            variant: "error",
          });
          setBalance(null);
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        setBalance(null);
      })
      .finally(() => setLoading(false));
  };
  const onSubmit = (data: FormData) => {
    if (Object.values(data)?.length) {
      setIsBtnEnabled(false);
      BankAccountProfileService.createAccountTransfer({
        ...data,
        fromAccountId: data.fromAccountId.value,
        toAccountId: data.toAccountId.value,
        amount: +data.amount,
      })
        .then((response: GetAccountTransferByIdResponse) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={t("transferred") + " " + t("successfully")}
              />,
              { variant: "success" },
            );
            navigate(-1);
          } else
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
        })
        .catch((err: any) => {
          console.error("===", err);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        })
        .finally(() => setIsBtnEnabled(true));
    }
  };
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setDropdownPage(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setDropdownPage(dropdownPage + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (typeName === "accounts" || typeName === "destinationAccounts")
        bankAccountsRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };
  return (
    <>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={true}
        onClick={() => navigate(-1)}
        onSubmit={handleSubmit(onSubmit)}
        heading={t("newInterAccountTransfer")}
        icon={
          <MoveDownOutlinedIcon
            sx={{
              fill: "#000",
              width: "30px",
              height: "30px",
              marginTop: "3px",
            }}
          />
        }
        btnText={"back"}
        btnType={"back"}
        isBtnEnabled={IsBtnEnabled}
        showCreateBtn={true}
        breadcrumbItems={breadcrumbItems}
      />
      <SimpleModal
        visable={true}
        padding={false}
        title={t("newInterAccountTransfer")}
        isButtonVisable={true}
        isVisableBtn={IsBtnEnabled}
        btnclosetext="Back"
        buttonText={"submit"}
        attBtnNotshow={false}
        notVisableBackbtn={true}
        showPortList={handleSubmit(onSubmit)}
        formSubmit={handleSubmit(onSubmit)}
        content={
          <Grid
            container
            component="form"
            id="form"
            className="px-[8px] items-baseline"
            rowGap={2.5}
            columnGap={4}
            onSubmit={handleSubmit(onSubmit)}
          >
            <Grid container item xs={12} gap={2.5} className="flex-nowrap">
              <Grid item xs={12} md={3}>
                <Controller
                  name="fromAccountId"
                  control={control}
                  defaultValue={null}
                  rules={{ required: t("This field is required.") }}
                  render={({ field }) => (
                    <DynamicSelect
                      label={t("from") + " " + t("ACCOUNT")}
                      defaultPlaceholder="phSelectSourceAccount"
                      {...field}
                      options={accounts}
                      isSearchable
                      isRequired
                      onChange={(selectedOption: any) => {
                        field.onChange(selectedOption);
                        setValue("toAccountId", null);
                        setLoading(true);
                        getAccountBalanceById(selectedOption?.value);
                      }}
                      error={errors.fromAccountId?.message as string}
                      isDisabled={false}
                      isLoading={searchLoading === "accounts"}
                      onInputChange={(
                        search: string,
                        { action }: { action: string },
                      ) => {
                        if (
                          action === "input-change" ||
                          (action !== "set-value" &&
                            action !== "menu-close" &&
                            action !== "input-blur")
                        )
                          handleDropdownActions("accounts", "searchIt", search);
                        else if (
                          action === "input-blur" ||
                          action === "menu-close"
                        ) {
                          if (dropdownSearchTerm || dropdownPage > 1)
                            handleDropdownActions("accounts", "searchIt", "");
                          else setDropdownSearchTerm("");
                          setDropdownPage(1);
                        }
                      }}
                      onMenuScrollToBottom={() => {
                        if (!(bankAccountsData?.pages[0]?.data as any).last)
                          handleDropdownActions("accounts", "loadNext");
                      }}
                      page={dropdownPage}
                    />
                  )}
                />
                {loading ? (
                  <Skeleton
                    animation="pulse"
                    sx={{
                      backgroundColor: themeColors?.secondary,
                      marginTop: "-10px",
                    }}
                  />
                ) : balance || balance === 0 ? (
                  <Typography className="font-Saira text-start font-[600] text-[15px]">
                    <span className="italic font-[500] text-[13px]">
                      {t("balance")}
                    </span>
                    <span>:</span>
                    <span
                      className="ms-[5px]"
                      style={{ color: themeColors?.primary }}
                    >
                      {new Intl.NumberFormat("en", {
                        notation: "standard",
                        maximumFractionDigits: 2,
                      }).format(balance || 0)}
                    </span>
                    <span
                      className="uppercase ms-[3px] font-[400] text-[11px]"
                      style={{ color: themeColors?.primary }}
                    >
                      {loggedInUserCurrency}
                    </span>
                  </Typography>
                ) : (
                  ""
                )}
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="toAccountId"
                  control={control}
                  defaultValue={null}
                  rules={{ required: t("This field is required.") }}
                  render={({ field }) => (
                    <DynamicSelect
                      label={t("to") + " " + t("ACCOUNT")}
                      defaultPlaceholder="phSelectDestinationAccount"
                      {...field}
                      options={destinationAccounts}
                      isSearchable
                      isRequired
                      onChange={(selectedOption: any) =>
                        field.onChange(selectedOption)
                      }
                      error={errors.toAccountId?.message as string}
                      isDisabled={watch("fromAccountId")?.value ? false : true}
                      isLoading={searchLoading === "destinationAccounts"}
                      onInputChange={(
                        search: string,
                        { action }: { action: string },
                      ) => {
                        if (
                          action === "input-change" ||
                          (action !== "set-value" &&
                            action !== "menu-close" &&
                            action !== "input-blur")
                        )
                          handleDropdownActions(
                            "destinationAccounts",
                            "searchIt",
                            search,
                          );
                        else if (
                          action === "input-blur" ||
                          action === "menu-close"
                        ) {
                          if (dropdownSearchTerm || dropdownPage > 1)
                            handleDropdownActions(
                              "destinationAccounts",
                              "searchIt",
                              "",
                            );
                          else setDropdownSearchTerm("");
                          setDropdownPage(1);
                        }
                      }}
                      onMenuScrollToBottom={() => {
                        if (
                          !((destinationAccounts as any)?.pages[0]?.data as any)
                            .last
                        )
                          handleDropdownActions(
                            "destinationAccounts",
                            "loadNext",
                          );
                      }}
                      page={dropdownPage}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  type="number"
                  {...register("amount", {
                    required: t("This field is required."),
                    validate: (value) =>
                      value > 0 || t("amountGreaterThanZero"),
                  })}
                  label={t("amount") + " *"}
                  placeholder={t("phTransferAmount")}
                  title={t("amount")}
                  fullWidth
                  error={!!errors.amount}
                  helperText={errors.amount?.message as string}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Grid>
        }
      />
    </>
  );
};

export default InterAccountTransferCreate;
